
import React, { useState, useMemo, useEffect } from 'react';
import type { NewsItem } from '../../../types';
import { 
    MegaphoneIcon, CalendarDaysIcon, SparklesIcon, 
    PlusIcon, ChevronRightIcon, ClockIcon,
    PencilSquareIcon, TrashIcon, PhotoIcon,
    XMarkIcon
} from '../../ui/Icons';
import { timeAgo } from '../../../utils/helpers';
import BottomSheet from '../../ui/BottomSheet';
import { newsAPI } from '../../../services';
import { useNotification } from '../../../App';

interface AdminPortalNewsPageProps {
    news: NewsItem[];
    refreshData: () => void;
}

const CATEGORY_CONFIG: Record<NewsItem['category'], { label: string, icon: React.ReactNode, color: string, bgColor: string }> = {
    notification: { 
        label: 'Thông báo', 
        icon: <MegaphoneIcon className="w-3.5 h-3.5" />, 
        color: 'text-blue-600',
        bgColor: 'bg-blue-50'
    },
    plan: { 
        label: 'Kế hoạch', 
        icon: <CalendarDaysIcon className="w-3.5 h-3.5" />, 
        color: 'text-orange-600',
        bgColor: 'bg-orange-50'
    },
    event: { 
        label: 'Sự kiện', 
        icon: <SparklesIcon className="w-3.5 h-3.5" />, 
        color: 'text-purple-600',
        bgColor: 'bg-purple-50'
    },
};

const AdminPortalNewsPage: React.FC<AdminPortalNewsPageProps> = ({ news = [], refreshData }) => {
    const { showToast } = useNotification();
    const [selectedNews, setSelectedNews] = useState<NewsItem | null>(null);
    const [isFormOpen, setIsFormOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // Form state
    const [formData, setFormData] = useState({
        title: '',
        content: '',
        category: 'notification' as NewsItem['category'],
        imageUrl: '',
        sender: 'Ban Quản Lý'
    });

    const sortedNews = useMemo(() => {
        return [...news].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    }, [news]);

    const handleOpenCreate = () => {
        setFormData({
            title: '',
            content: '',
            category: 'notification',
            imageUrl: '',
            sender: 'Ban Quản Lý'
        });
        setIsEditing(false);
        setIsFormOpen(true);
    };

    const handleOpenEdit = (item: NewsItem) => {
        setFormData({
            title: item.title,
            content: item.content,
            category: item.category,
            imageUrl: item.imageUrl || '',
            sender: item.sender || 'Ban Quản Lý'
        });
        setIsEditing(true);
        setIsFormOpen(true);
        setSelectedNews(null); // Close detail view
    };

    const handleDelete = async (id: string) => {
        if (!window.confirm('Bạn có chắc chắn muốn xóa tin tức này?')) return;
        
        setIsLoading(true);
        try {
            await newsAPI.deleteNewsItem(id);
            showToast('Đã xóa tin tức thành công', 'success');
            refreshData();
            setSelectedNews(null);
        } catch (error) {
            showToast('Lỗi khi xóa tin tức', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.title || !formData.content) {
            showToast('Vui lòng điền đầy đủ thông tin', 'error');
            return;
        }

        setIsLoading(true);
        try {
            const newsData: Partial<NewsItem> = {
                ...formData,
                date: isEditing && selectedNews ? selectedNews.date : new Date().toISOString(),
            };

            if (isEditing && selectedNews) {
                await newsAPI.saveNewsItem({ ...newsData, id: selectedNews.id } as NewsItem);
                showToast('Đã cập nhật tin tức', 'success');
            } else {
                await newsAPI.saveNewsItem(newsData as Omit<NewsItem, 'id'>);
                showToast('Đã đăng tin tức mới', 'success');
            }
            
            setIsFormOpen(false);
            refreshData();
        } catch (error) {
            showToast('Lỗi khi lưu tin tức', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50">
            {/* Header / Actions */}
            <div className="p-4 flex justify-between items-center bg-white border-b border-gray-100 sticky top-0 z-10">
                <h3 className="text-[10px] font-black text-gray-400 uppercase tracking-widest">Danh sách tin tức</h3>
                <button 
                    onClick={handleOpenCreate}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-lg shadow-sm active:scale-95 transition-all"
                >
                    <PlusIcon className="w-3 h-3" /> Đăng tin
                </button>
            </div>

            {/* News List */}
            <div className="p-4 space-y-3 pb-24">
                {sortedNews.length > 0 ? sortedNews.map(item => {
                    const config = CATEGORY_CONFIG[item.category];
                    return (
                        <div 
                            key={item.id} 
                            onClick={() => setSelectedNews(item)}
                            className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 active:scale-[0.98] transition-all flex flex-col gap-3"
                        >
                            <div className="flex justify-between items-start">
                                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${config.bgColor} ${config.color} border border-current/10`}>
                                    {config.icon}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{config.label}</span>
                                </div>
                                <div className="flex items-center gap-1 text-[9px] font-bold text-gray-400">
                                    <ClockIcon className="w-3 h-3" />
                                    {timeAgo(item.date)}
                                </div>
                            </div>
                            
                            <div className="flex gap-4">
                                {item.imageUrl && (
                                    <div className="w-20 h-20 rounded-xl overflow-hidden shrink-0 border border-gray-100">
                                        <img src={item.imageUrl} alt={item.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <h4 className="text-sm font-black text-gray-800 line-clamp-2 leading-tight mb-1">{item.title}</h4>
                                    <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.content}</p>
                                </div>
                            </div>

                            <div className="flex justify-between items-center pt-2 border-t border-gray-50">
                                <span className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Gửi bởi: {item.sender || 'BQL'}</span>
                                <ChevronRightIcon className="w-4 h-4 text-gray-200" />
                            </div>
                        </div>
                    );
                }) : (
                    <div className="text-center py-12">
                        <MegaphoneIcon className="w-12 h-12 text-gray-100 mx-auto mb-3" />
                        <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Chưa có tin tức nào</p>
                    </div>
                )}
            </div>

            {/* News Detail Bottom Sheet */}
            <BottomSheet 
                isOpen={!!selectedNews} 
                onClose={() => setSelectedNews(null)} 
                title="Chi tiết tin tức"
            >
                {selectedNews && (
                    <div className="space-y-6 pb-6">
                        {selectedNews.imageUrl && (
                            <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm aspect-video bg-gray-50">
                                <img src={selectedNews.imageUrl} alt={selectedNews.title} className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            </div>
                        )}
                        
                        <div className="space-y-2">
                            <div className="flex items-center gap-2">
                                <div className={`flex items-center gap-2 px-2 py-1 rounded-lg ${CATEGORY_CONFIG[selectedNews.category].bgColor} ${CATEGORY_CONFIG[selectedNews.category].color} border border-current/10`}>
                                    {CATEGORY_CONFIG[selectedNews.category].icon}
                                    <span className="text-[9px] font-black uppercase tracking-tight">{CATEGORY_CONFIG[selectedNews.category].label}</span>
                                </div>
                                <span className="text-[10px] font-bold text-gray-400">{new Date(selectedNews.date).toLocaleString('vi-VN')}</span>
                            </div>
                            <h2 className="text-xl font-black text-gray-900 leading-tight">{selectedNews.title}</h2>
                        </div>

                        <div className="prose prose-sm max-w-none text-gray-600 leading-relaxed font-medium">
                            {selectedNews.content}
                        </div>

                        <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                            <div className="w-10 h-10 bg-white rounded-xl flex items-center justify-center shadow-sm text-primary">
                                <MegaphoneIcon className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-[9px] font-black text-gray-400 uppercase tracking-widest">Người gửi</p>
                                <p className="text-sm font-black text-gray-800">{selectedNews.sender || 'Ban Quản Lý'}</p>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <button 
                                onClick={() => handleOpenEdit(selectedNews)}
                                className="flex items-center justify-center gap-2 py-3 bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-widest rounded-xl active:scale-95 transition-all"
                            >
                                <PencilSquareIcon className="w-4 h-4" /> Chỉnh sửa
                            </button>
                            <button 
                                onClick={() => handleDelete(selectedNews.id)}
                                className="flex items-center justify-center gap-2 py-3 bg-red-50 text-red-600 text-[10px] font-black uppercase tracking-widest rounded-xl active:scale-95 transition-all"
                            >
                                <TrashIcon className="w-4 h-4" /> Xóa tin
                            </button>
                        </div>
                    </div>
                )}
            </BottomSheet>

            {/* Create/Edit News Bottom Sheet */}
            <BottomSheet 
                isOpen={isFormOpen} 
                onClose={() => setIsFormOpen(false)} 
                title={isEditing ? "Chỉnh sửa tin tức" : "Đăng tin mới"}
            >
                <form onSubmit={handleSubmit} className="space-y-5 pb-6">
                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Tiêu đề</label>
                        <input 
                            type="text"
                            value={formData.title}
                            onChange={(e) => setFormData({...formData, title: e.target.value})}
                            placeholder="Nhập tiêu đề tin tức..."
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Danh mục</label>
                        <div className="grid grid-cols-3 gap-2">
                            {(Object.keys(CATEGORY_CONFIG) as NewsItem['category'][]).map(cat => (
                                <button
                                    key={cat}
                                    type="button"
                                    onClick={() => setFormData({...formData, category: cat})}
                                    className={`py-2 rounded-lg border text-[9px] font-black uppercase tracking-tight flex flex-col items-center gap-1 transition-all ${
                                        formData.category === cat 
                                        ? 'bg-primary border-primary text-white shadow-sm' 
                                        : 'bg-white border-gray-100 text-gray-400'
                                    }`}
                                >
                                    {CATEGORY_CONFIG[cat].icon}
                                    {CATEGORY_CONFIG[cat].label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Nội dung</label>
                        <textarea 
                            rows={5}
                            value={formData.content}
                            onChange={(e) => setFormData({...formData, content: e.target.value})}
                            placeholder="Nhập nội dung tin tức..."
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all resize-none"
                        />
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Link ảnh (URL)</label>
                        <div className="relative">
                            <input 
                                type="text"
                                value={formData.imageUrl}
                                onChange={(e) => setFormData({...formData, imageUrl: e.target.value})}
                                placeholder="https://example.com/image.jpg"
                                className="w-full pl-10 pr-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                            />
                            <PhotoIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                        </div>
                        {formData.imageUrl && (
                            <div className="mt-2 rounded-xl overflow-hidden border border-gray-100 aspect-video bg-gray-50">
                                <img src={formData.imageUrl} alt="Preview" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                            </div>
                        )}
                    </div>

                    <div className="space-y-1.5">
                        <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest ml-1">Người gửi</label>
                        <input 
                            type="text"
                            value={formData.sender}
                            onChange={(e) => setFormData({...formData, sender: e.target.value})}
                            className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition-all"
                        />
                    </div>

                    <div className="pt-2 flex gap-3">
                        <button 
                            type="button"
                            onClick={() => setIsFormOpen(false)}
                            className="flex-1 py-3 bg-gray-100 text-gray-600 text-[10px] font-black uppercase tracking-widest rounded-xl active:scale-95 transition-all"
                        >
                            Hủy
                        </button>
                        <button 
                            type="submit"
                            disabled={isLoading}
                            className="flex-[2] py-3 bg-primary text-white text-[10px] font-black uppercase tracking-widest rounded-xl shadow-md active:scale-95 transition-all disabled:opacity-50"
                        >
                            {isLoading ? 'Đang xử lý...' : (isEditing ? 'Cập nhật' : 'Đăng tin')}
                        </button>
                    </div>
                </form>
            </BottomSheet>
        </div>
    );
};

export default AdminPortalNewsPage;
